'use client';

import { createClient } from '@/lib/supabase/client';

export type Stream = 'Engineering' | 'Medical' | 'Management' | 'Law' | 'Arts' | 'Commerce' | 'Science';
export type Ownership = 'Government' | 'Private' | 'Deemed' | 'Autonomous';
export type Accreditation = 'NAAC A++' | 'NAAC A+' | 'NAAC A' | 'NAAC B++' | 'NBA';

export interface Course {
  id: string;
  name: string;
  level: 'UG' | 'PG' | 'Diploma' | 'Doctorate';
  duration: string;
  totalFees: number;
  stream: Stream;
  eligibility: string;
  totalSeats: number;
}

export interface CategoryCutoff {
  exam: string;
  year: number;
  general: number;
  obc: number;
  sc: number;
  st: number;
  ews: number;
  unit: string;
}

export interface College {
  id: string;
  name: string;
  shortName: string;
  location: string;
  state: string;
  established: number;
  ownership: Ownership;
  streams: Stream[];
  nirfRank: number | null;
  nircRank?: number;
  accreditation: Accreditation;
  rating: number;
  reviewCount: number;
  totalFees: number;
  feesPerYear: number;
  cutoffExam: string;
  cutoffScore: number;
  cutoffUnit: string;
  placementAvgPackage: number;
  placementHighestPackage: number;
  image: string;
  logoColor: string;
  courses: Course[];
  hostel: boolean;
  scholarships: boolean;
  status: 'Active' | 'Pending' | 'Inactive';
  cutoffs: CategoryCutoff[];
}

function mapRow(row: any, courses: Course[], cutoffs: CategoryCutoff[]): College {
  return {
    id: row.id,
    name: row.name,
    shortName: row.short_name,
    location: row.location,
    state: row.state,
    established: row.established,
    ownership: row.ownership as Ownership,
    streams: (row.streams || []) as Stream[],
    nirfRank: row.nirf_rank,
    nircRank: row.nirc_rank,
    accreditation: row.accreditation as Accreditation,
    rating: Number(row.rating),
    reviewCount: row.review_count,
    totalFees: row.total_fees,
    feesPerYear: row.fees_per_year,
    cutoffExam: row.cutoff_exam,
    cutoffScore: Number(row.cutoff_score),
    cutoffUnit: row.cutoff_unit,
    placementAvgPackage: row.placement_avg_package,
    placementHighestPackage: row.placement_highest_package,
    image: row.image,
    logoColor: row.logo_color,
    hostel: row.hostel,
    scholarships: row.scholarships,
    status: row.college_status as 'Active' | 'Pending' | 'Inactive',
    courses,
    cutoffs,
  };
}

function mapCourse(c: any): Course {
  return {
    id: c.id,
    name: c.name,
    level: c.course_level as Course['level'],
    duration: c.duration,
    totalFees: c.total_fees,
    stream: c.stream as Stream,
    eligibility: c.eligibility,
    totalSeats: c.total_seats,
  };
}

function mapCutoff(c: any): CategoryCutoff {
  return {
    exam: c.exam_name,
    year: c.cutoff_year,
    general: Number(c.general),
    obc: Number(c.obc),
    sc: Number(c.sc),
    st: Number(c.st),
    ews: Number(c.ews),
    unit: c.unit,
  };
}

export async function fetchColleges(): Promise<College[]> {
  const supabase = createClient();
  try {
    const results = await Promise.all([
      supabase.from('colleges').select('*').order('nirf_rank', { ascending: true, nullsFirst: false }),
      supabase.from('courses').select('*'),
      supabase.from('cutoffs').select('*'),
    ]);

    const { data: collegeRows, error: cErr } = results[0];
    const { data: courseRows, error: crErr } = results[1];
    const { data: cutoffRows, error: ctErr } = results[2];

    if (cErr || crErr || ctErr) {
      console.error('fetchColleges error:', cErr?.message || crErr?.message || ctErr?.message);
      return [];
    }

    return (collegeRows || []).map((row) => {
      const courses = (courseRows || []).filter((c) => c.college_id === row.id).map(mapCourse);
      const cutoffs = (cutoffRows || []).filter((c) => c.college_id === row.id).map(mapCutoff);
      return mapRow(row, courses, cutoffs);
    });
  } catch (err: any) {
    console.error('fetchColleges exception:', err.message);
    return [];
  }
}

export async function fetchCollegeById(id: string): Promise<College | null> {
  const supabase = createClient();
  try {
    const results = await Promise.all([
      supabase.from('colleges').select('*').eq('id', id).maybeSingle(),
      supabase.from('courses').select('*').eq('college_id', id),
      supabase.from('cutoffs').select('*').eq('college_id', id),
    ]);

    const { data: row, error: cErr } = results[0];
    const { data: courseRows, error: crErr } = results[1];
    const { data: cutoffRows, error: ctErr } = results[2];

    if (cErr || crErr || ctErr || !row) {
      console.error('fetchCollegeById error:', cErr?.message || crErr?.message || ctErr?.message);
      return null;
    }

    const courses = (courseRows || []).map(mapCourse);
    const cutoffs = (cutoffRows || []).map(mapCutoff);
    return mapRow(row, courses, cutoffs);
  } catch (err: any) {
    console.error('fetchCollegeById exception:', err.message);
    return null;
  }
}
